import type { Identifier, RaRecord } from "ra-core";
import type { ComponentType } from "react";
import type { WorkflowAction, WorkflowStep } from "@slate/shared";

import type {
  COMPANY_CREATED,
  CONTACT_CREATED,
  CONTACT_NOTE_CREATED,
  DEAL_CREATED,
  DEAL_NOTE_CREATED,
} from "./consts";

export type SignUpData = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
};

export type SalesFormData = {
  avatar?: string;
  email: string;
  password?: string;
  first_name: string;
  last_name: string;
  administrator: boolean;
  disabled: boolean;
};

export type Sale = {
  first_name: string;
  last_name: string;
  administrator: boolean;
  avatar?: RAFile;
  disabled?: boolean;
  user_id: string;

  /**
   * This is a copy of the user's email, to make it easier to handle by react admin
   * DO NOT UPDATE this field directly, it should be updated by the backend
   */
  email: string;

  /**
   * This is used by the fake rest provider to store the password
   * DO NOT USE this field in your code besides the fake rest provider
   * @deprecated
   */
  password?: string;
} & Pick<RaRecord, "id">;

export type Company = {
  name: string;
  logo: RAFile;
  sector: string;
  size: 1 | 10 | 50 | 250 | 500;
  linkedin_url: string;
  website: string;
  phone_number: string;
  address: string;
  zipcode: string;
  city: string;
  state_abbr: string;
  sales_id?: Identifier;
  created_at: string;
  description: string;
  revenue: string;
  tax_identifier: string;
  country: string;
  context_links?: string[];
  nb_contacts?: number;
  nb_deals?: number;
  /** Parent (holding) company. Chains can nest to any depth. */
  parent_company_id?: Identifier | null;
  tags?: number[];
  /** Tenant-defined custom field values, keyed by slug (see CustomFieldDef). */
  custom_fields?: Record<string, string | number | null> | null;
} & Pick<RaRecord, "id">;

export type EmailAndType = {
  email: string;
  type: "Work" | "Home" | "Other";
};

export type PhoneNumberAndType = {
  number: string;
  type: "Work" | "Home" | "Other";
};

export type Contact = {
  first_name: string;
  last_name: string;
  title: string;
  company_id?: Identifier | null;
  email_jsonb: EmailAndType[];
  /**
   * `src` is tried first, then `fallback` (the email domain's favicon), then
   * initials. The database fills both without any network call (R3).
   */
  avatar?: Partial<RAFile> & { fallback?: string | null };
  linkedin_url?: string | null;
  first_seen: string;
  last_seen: string;
  has_newsletter: boolean;
  tags: number[];
  gender: string;
  sales_id?: Identifier;
  status: string;
  /** Funnel position; the ladder is tenant-configurable (lifecycleStages). */
  lifecycle_stage?: string;
  background: string;
  phone_jsonb: PhoneNumberAndType[];
  nb_tasks?: number;
  company_name?: string;
  /** Attribution journey, oldest first. Demo data; production reads the spine. */
  touches?: ContactTouch[];
  /** Persisted lead score (0-100), computed by the automation worker from the scoring rules. */
  score?: number;
  /** Manual adjust_score deltas, added on top of the rules sum by the worker. */
  score_manual?: number;
  /** CAN-SPAM: opted out of marketing email; automations suppress sends. */
  email_opt_out?: boolean;
  /** Original source (attribution, decision #26); null until capture lands. */
  source?: string | null;
  /** Tenant-defined custom field values, keyed by slug (see CustomFieldDef). */
  custom_fields?: Record<string, string | number | null> | null;
} & Pick<RaRecord, "id">;

export type ContactNote = {
  contact_id: Identifier;
  text: string;
  date: string;
  sales_id: Identifier;
  status: string;
  attachments?: AttachmentNote[];
  /** What kind of activity this logs: note, call, email, meeting, linkedin. */
  activity_type?: string;
} & Pick<RaRecord, "id">;

export type Deal = {
  name: string;
  company_id: Identifier;
  contact_ids: Identifier[];
  category: string;
  stage: string;
  description: string;
  amount: number;
  created_at: string;
  updated_at: string;
  archived_at?: string;
  expected_closing_date: string;
  sales_id: Identifier;
  index: number;
  /** Stamped when the deal moves to won/lost - honest close-date reporting. */
  closed_at?: string | null;
  /** Why it was lost (tenant-configurable closeReasons). */
  close_reason?: string | null;
  /** Every stage transition, stamped: powers days-in-stage and velocity. */
  stage_history?: { stage: string; date: string }[];
  /** Tenant-defined custom field values, keyed by slug (see CustomFieldDef). */
  custom_fields?: Record<string, string | number | null> | null;
} & Pick<RaRecord, "id">;

export type DealNote = {
  deal_id: Identifier;
  text: string;
  date: string;
  sales_id: Identifier;
  attachments?: AttachmentNote[];
  /** What kind of activity this logs: note, call, email, meeting, linkedin. */
  activity_type?: string;

  // This is defined for compatibility with `ContactNote`
  status?: undefined;
} & Pick<RaRecord, "id">;

export type Tag = {
  id: number;
  name: string;
  color: string;
};

export type Task = {
  /** Tasks are polymorphic: attach to a contact, a deal, or a company. */
  contact_id?: Identifier | null;
  deal_id?: Identifier | null;
  company_id?: Identifier | null;
  type: string;
  text: string;
  due_date: string;
  done_date?: string | null;
  sales_id?: Identifier;
} & Pick<RaRecord, "id">;

export type Notification = {
  /** The recipient - who this notification is FOR, set by the automation worker. */
  sales_id: Identifier;
  contact_id?: Identifier | null;
  title: string;
  body?: string | null;
  /** In-app path this notification links to, e.g. `/contacts/123/show`. */
  link?: string | null;
  read_at?: string | null;
  created_at: string;
} & Pick<RaRecord, "id">;

export type ActivityCompanyCreated = {
  type: typeof COMPANY_CREATED;
  company_id: Identifier;
  company: Company;
  sales_id: Identifier;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityContactCreated = {
  type: typeof CONTACT_CREATED;
  company_id: Identifier;
  sales_id?: Identifier;
  contact: Contact;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityContactNoteCreated = {
  type: typeof CONTACT_NOTE_CREATED;
  sales_id?: Identifier;
  contactNote: ContactNote;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityDealCreated = {
  type: typeof DEAL_CREATED;
  company_id: Identifier;
  sales_id?: Identifier;
  deal: Deal;
  date: string;
};

export type ActivityDealNoteCreated = {
  type: typeof DEAL_NOTE_CREATED;
  sales_id?: Identifier;
  dealNote: DealNote;
  date: string;
};

export type Activity = RaRecord &
  (
    | ActivityCompanyCreated
    | ActivityContactCreated
    | ActivityContactNoteCreated
    | ActivityDealCreated
    | ActivityDealNoteCreated
  );

export interface RAFile {
  src: string;
  title: string;
  path?: string;
  rawFile: File;
  type?: string;
}

export type AttachmentNote = RAFile;

export interface LabeledValue {
  value: string;
  label: string;
}

export interface CustomFieldDef {
  /** Storage key; values live on records as `custom_<key>`. Slugged from label. */
  key?: string;
  label: string;
  object: "contacts" | "companies" | "deals";
  type: "text" | "number" | "date" | "select";
  /** Comma-separated choices, for type "select". */
  options?: string;
  /** Display format for type "number" - defaults to "plain" (no key = plain). */
  format?: "plain" | "currency";
}

export interface SlateFormField {
  id: string;
  /** Contact attribute this maps to: core field key or custom_<key>. */
  source: string;
  label: string;
  type: "text" | "email" | "phone" | "textarea" | "select" | "checkbox" | "hidden" | "pagebreak";
  required: boolean;
  /** Comma-separated choices for select fields. */
  options?: string;
  placeholder?: string;
  /** Fixed value for hidden fields (e.g. source tagging). */
  value?: string;
}

export interface SlateForm {
  id: string;
  name: string;
  fields: SlateFormField[];
  submitLabel: string;
  successMessage: string;
  /** Redirect here after submit instead of showing the success message. */
  redirectUrl?: string;
  /** Require a Cloudflare Turnstile pass (site key set in configuration). */
  captcha?: boolean;
  design?: {
    buttonColor?: string;
    buttonTextColor?: string;
    inputRadius?: number;
    /** "inherit" uses the host site's font (default); otherwise a stack name. */
    font?: "inherit" | "system" | "georgia";
  };
}

export interface EmailBlock {
  id: string;
  type: "heading" | "text" | "button" | "image" | "divider" | "spacer" | "logo" | "bullets";
  text?: string;
  url?: string;
  align?: "left" | "center" | "right";
  /** heading/text/bullets: text color override. */
  color?: string;
  /** heading/text: relative size. */
  size?: "s" | "m" | "l";
  /** image blocks: accessible description + display width */
  alt?: string;
  width?: "full" | "half";
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  /** Hidden preview text shown after the subject in inboxes. */
  preheader?: string;
  accentColor: string;
  /** Email-safe font stack for the whole template. */
  fontFamily?: "arial" | "georgia" | "verdana" | "trebuchet";
  /** Page background behind the card. */
  backgroundColor?: string;
  /** Card (content) background. */
  cardColor?: string;
  blocks: EmailBlock[];
}

export interface ContactListDef {
  id: string;
  name: string;
  logic: "all" | "any";
  filters: {
    id: number;
    field: string;
    op: string;
    value: string;
    value2?: string;
  }[];
}

// Workflow and sequence definitions live in packages/shared (R2), shared
// with the automation worker that runs them.
export type {
  AssigneeRule,
  BranchCondition,
  SequenceDef,
  SequenceStepDef,
  WorkflowAction,
  WorkflowBranch,
  WorkflowDef,
  WorkflowDelay,
  WorkflowStep,
} from "@slate/shared";

/** Presentation types shared by the report builder and dashboard tiles. */
export type ChartType =
  | "stat"
  | "column"
  | "bar"
  | "line"
  | "area"
  | "donut"
  | "funnel"
  | "table";

export interface DashboardWidget {
  id: string;
  /** Name of a saved report (from the report builder). */
  reportName: string;
  size: "half" | "full";
  /** Overrides the saved report's chart type for this tile only. */
  chartType?: ChartType;
}

export interface DashboardDef {
  id: string;
  name: string;
  widgets: DashboardWidget[];
}

/** A native lead-scoring rule: signal → points. Editable per tenant. */
export interface ScoringRule {
  key: string;
  label: string;
  points: number;
  enabled?: boolean;
}

/** Every executable action in a workflow tree, then-arms and else-arms included. */
export const flattenWorkflowActions = (steps: WorkflowStep[]): WorkflowAction[] =>
  steps.flatMap((s) =>
    s.type === "branch"
      ? [...flattenWorkflowActions(s.then), ...flattenWorkflowActions(s.otherwise)]
      : s.type === "delay"
        ? []
        : [s],
  );

/** One section of a record page's about rail - orderable, hideable per tenant. */
export interface SidebarModulePref {
  key: string;
  hidden?: boolean;
}

/** A marketing touch on a contact's journey (production: touch.captured events). */
export interface ContactTouch {
  date: string;
  channel: "organic" | "paid" | "social" | "email" | "direct" | "referral";
  source: string;
  medium?: string;
  landingPage?: string;
  campaign?: string;
}

export interface IntegrationConfig {
  enabled?: boolean;
  fields?: Record<string, string>;
}

/**
 * Thresholds behind the dashboard's "Keep an eye on" panel - what counts as
 * heating up, slipping, or overdue. Tunable in Settings → Keep an eye on.
 */
export interface SignalsConfig {
  heatingUp: {
    enabled: boolean;
    /** Minimum lead score (0-100) to qualify. */
    minScore: number;
    /** Contact must have been active within this many days. */
    activeDays: number;
  };
  slipping: {
    enabled: boolean;
    /** Open deal untouched for at least this many days. */
    quietDays: number;
    /** Ignore deals below this amount (0 = show all). */
    minAmount: number;
  };
  overdue: {
    enabled: boolean;
    /** Grace period after the due date before a task counts as overdue. */
    graceDays: number;
  };
  /** How many items each column shows. */
  perColumn: number;
}

/** Per-recipient engagement on a sent campaign (production: spine events). */
export interface CampaignEngagementRow {
  contactId: Identifier;
  name: string;
  opened: boolean;
  opens: number;
  clickedLinks: string[];
}

export interface CampaignAnalytics {
  delivered: number;
  /** Unique recipients who opened / total open events. */
  uniqueOpens: number;
  totalOpens: number;
  /** Unique recipients who clicked at least one link. */
  uniqueClicks: number;
  links: { url: string; clicks: number }[];
  unsubscribes: number;
  engagement: CampaignEngagementRow[];
}

export interface CampaignDef {
  id: string;
  name: string;
  templateId?: string;
  listId?: string;
  status: "draft" | "sent";
  sentAt?: string;
  recipients?: number;
  /** Opens via tracking pixel, clicks via wrapped links - recorded per send. */
  analytics?: CampaignAnalytics;
}

export interface DealStage extends LabeledValue {
  /** Win probability for this stage, 0-100. Used for weighted pipeline forecasts. */
  winProbability?: number;
}

export interface NoteStatus extends LabeledValue {
  color: string;
}

export interface ContactGender {
  value: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}
